import { Box, Divider, MenuItem, Stack, Typography } from '@mui/material';
import type { GridSortModel } from '@mui/x-data-grid';
import { TextField, toast, useIsMobile } from '@side/pantry';
import { format } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@/components/DataGrid';
import type { PaymentSchema } from '$api-clients/generated';
import { ExportToCSVButton } from '../../components/ExportToCSVButton';
import { PAGE_SIZE_OPTIONS } from '../../DealAllocationsTable/DealAllocationsTable.helpers';
import {
  buildReportingSummaryPerformanceQuery,
  createLoadingRows,
  fetchAllPagesReportingSummaryPages,
  fetchUsersAndTeamsMaps,
  isReportingPeriodAllTime,
  isReportingPeriodL365,
  mapDataGridColumnToValueOrEmpty,
  shouldUseYearlyReportingPeriod,
} from '../../helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import { useReportsRole } from '../../ReportsConfiguration/useReportsRole';
import {
  buildCsv,
  downloadCsv,
} from '../../ReportsExport/ReportsExport.helper';
import { TablePagination } from '../../TablePagination/TablePagination';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';
import { useGoalsAvailable } from '../hooks/useGoalsAvailable';
import {
  TEAM_PERFORMANCE_DEFAULT_PAGE_SIZE,
  TEAM_PERFORMANCE_DEFAULT_SORT_MODEL,
  useTeamPerformance,
} from '../hooks/useTeamPerformance';
import {
  AdjustedGCIColumn,
  AgentColumns,
  filterGoalColumns,
  HIDDEN_BY_DEFAULT_COLUMN_FIELDS,
  TeamColumns,
} from './TeamPerformanceTable.columns';

export const TeamPerformanceTable = ({
  viewReportAs,
}: {
  viewReportAs: string;
}) => {
  const isMobile = useIsMobile('md');
  const [sortModel, setSortModel] = useState<GridSortModel>(
    TEAM_PERFORMANCE_DEFAULT_SORT_MODEL,
  );
  const [groupedBy, setGroupedBy] = useState<'AGENT' | 'TEAM'>('AGENT');

  const {
    rows,
    loading,
    totalCount,
    rowCount,
    paginationModel,
    setPaginationModel,
  } = useTeamPerformance({ viewReportAs, sortModel, groupedBy });

  // Keep the last known totalCount stable so the DataGrid doesn't reset to
  // page 0 when data is loading (totalCount is briefly undefined).
  const rowCountRef = useRef(0);
  if (totalCount !== undefined) rowCountRef.current = totalCount;

  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();
  const isL365 = isReportingPeriodL365(filters?.reportingPeriod);
  const isAllTime = isReportingPeriodAllTime(filters?.reportingPeriod);
  const hidePercentToGoal = isL365 || isAllTime;
  const { goalsAvailable } = useGoalsAvailable({ viewReportAs });
  const useYearlyPeriod = shouldUseYearlyReportingPeriod(
    filters?.reportingPeriod,
  );
  const { isPartner, isTeamLead } = useReportsRole();
  const [isExporting, setIsExporting] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >(Object.fromEntries(HIDDEN_BY_DEFAULT_COLUMN_FIELDS.map((f) => [f, false])));
  const isGroupedByAgent = groupedBy === 'AGENT';

  const teamColumnsWithGciReplacement = useMemo(() => {
    const columns = [...TeamColumns];

    if (isPartner || isTeamLead) {
      const weightedGciColumnIndex = columns.findIndex(
        (column) => column.field === 'weightedAdjustedGrossCommission',
      );
      if (weightedGciColumnIndex >= 0) {
        columns[weightedGciColumnIndex] = AdjustedGCIColumn;
      }
    }

    return columns;
  }, [isPartner, isTeamLead]);

  const agentColumnsToShow = useMemo(
    () =>
      filterGoalColumns(AgentColumns, { goalsAvailable, hidePercentToGoal }),
    [goalsAvailable, hidePercentToGoal],
  );
  const teamColumnsToShow = useMemo(
    () =>
      filterGoalColumns(teamColumnsWithGciReplacement, {
        goalsAvailable,
        hidePercentToGoal,
      }),
    [goalsAvailable, hidePercentToGoal, teamColumnsWithGciReplacement],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      // Reset to page 0 so the new sort starts from the beginning. React 18
      // batches these two setState calls into a single render, preventing a
      // double fetch.
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [setPaginationModel],
  );

  const handleExportCSV = useCallback(async () => {
    setIsExporting(true);
    try {
      const query = buildReportingSummaryPerformanceQuery(
        filters,
        viewReportAs,
        allTimeStartDate,
      );
      const results = await fetchAllPagesReportingSummaryPages({
        offset: 0,
        query: {
          ...query,
          ...(useYearlyPeriod ? { period: 'YEARLY' } : undefined),
          sortBy: sortModel[0]
            .field as PaymentSchema.paths['/reporting/summary']['get']['parameters']['query']['sortBy'],
          sortDirection: sortModel[0].sort === 'asc' ? 'ASC' : 'DESC',
          groupBy: [groupedBy],
          includePartiesWithoutTransactions: true,
        },
      });

      if (!results) {
        throw new Error('No results found');
      }

      const { usersById, teamsById } = await fetchUsersAndTeamsMaps({
        userIds: isGroupedByAgent
          ? results.map((r) => r.teamMemberIdentityId)
          : [],
        teamIds: !isGroupedByAgent ? results.map((r) => r.teamIdentityId) : [],
      });

      const resultsWithIdentityData = results.map((r) => ({
        ...r,
        agent: r.teamMemberIdentityId
          ? usersById[r.teamMemberIdentityId]?.displayName
          : undefined,
        team: r.teamIdentityId
          ? (teamsById[r.teamIdentityId]?.legalName ??
            teamsById[r.teamIdentityId]?.alias ??
            undefined)
          : undefined,
      }));

      const csv = buildCsv(
        resultsWithIdentityData,
        groupedBy === 'AGENT' ? agentColumnsToShow : teamColumnsToShow,
      );
      const filename = `Team-Performance-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      downloadCsv(csv, filename);
      toast('success', 'Team Performance Report exported successfully', {
        showCloseButton: true,
      });
    } catch {
      toast(
        'error',
        'Export failed. Please try again or contact Side Support for assistance.',
        { showCloseButton: true },
      );
    } finally {
      setIsExporting(false);
    }
  }, [
    groupedBy,
    isGroupedByAgent,
    agentColumnsToShow,
    teamColumnsToShow,
    filters,
    sortModel,
    viewReportAs,
    allTimeStartDate,
    useYearlyPeriod,
  ]);

  const columns = useMemo(() => {
    return (groupedBy === 'AGENT' ? agentColumnsToShow : teamColumnsToShow).map(
      (column) =>
        mapDataGridColumnToValueOrEmpty(column, ['percentToSalesVolumeGoal']),
    );
  }, [groupedBy, agentColumnsToShow, teamColumnsToShow]);

  const showPagination =
    totalCount && rowCount && totalCount > TEAM_PERFORMANCE_DEFAULT_PAGE_SIZE;

  return (
    <Stack sx={{ gap: 2 }} id="team-performance-table">
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            gap: 2,
            flexDirection: 'row',
          }}
        >
          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            Team Performance
          </Typography>
          <TextField
            select
            value={groupedBy}
            onChange={(e) => {
              setGroupedBy(e.target.value as 'AGENT' | 'TEAM');
              setPaginationModel({
                page: 0,
                pageSize: TEAM_PERFORMANCE_DEFAULT_PAGE_SIZE,
              });
              setSortModel(TEAM_PERFORMANCE_DEFAULT_SORT_MODEL);
            }}
            size="small"
            sx={{ '& .MuiInputBase-root': { height: { xs: 31, md: 'auto' } } }}
            slotProps={{
              select: {
                renderValue: (value) => (
                  <Stack
                    sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      Group by
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {value === 'AGENT' ? 'Agent' : 'Team'}
                    </Typography>
                  </Stack>
                ),
              },
            }}
          >
            <MenuItem value="AGENT">Agent</MenuItem>
            <MenuItem value="TEAM">Team</MenuItem>
          </TextField>
        </Stack>

        <ExportToCSVButton
          loading={isExporting}
          onClick={handleExportCSV}
          disabled={totalCount === 0}
          sx={{ height: 'fit-content' }}
        />
      </Stack>
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          sx={{
            '& .MuiDataGrid-row': { pointerEvents: 'none' },
            ...(totalCount === 0 && {
              '--DataGrid-overlayHeight': '60px',
            }),
            '& .MuiTablePagination-root': {
              justifyContent: 'flex-start',
              display: 'flex',
            },
            '& .MuiToolbar-root': {
              paddingLeft: 0,
            },
          }}
          disableColumnFilter
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingMode="server"
          pagination
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rows={
            loading
              ? createLoadingRows(paginationModel.pageSize, (i) => ({
                  // getRowId uses teamMemberIdentityId ?? teamIdentityId, so each
                  // loading row needs a unique value in one of those fields.
                  teamMemberIdentityId: String(i),
                }))
              : rows
          }
          rowCount={rowCountRef.current}
          loading={loading}
          paginationMode="server"
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowId={(row) => row.teamMemberIdentityId ?? row.teamIdentityId}
          slotProps={{
            columnsManagement: {
              disableShowHideToggle: false,
              disableResetButton: true,
            },
          }}
          slots={{
            pagination: () => (
              <TablePagination
                variant="performance"
                hidePaginationControls={!showPagination}
              />
            ),
            noRowsOverlay: () => (
              <Typography
                variant="body3"
                sx={{
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  color: 'text.secondary',
                }}
              >
                No results match the current filters
              </Typography>
            ),
          }}
        />
      </Box>
    </Stack>
  );
};
